import type { ShoppingCountryValue } from '@/redux/slices/storefrontContextSlice';

/**
 * Chat namespace keys for isolating different chat conversations in Redux state
 */
export const CHAT_NAMESPACES = {
  GIGAWATT_COPILOTKIT: 'gigawatt-copilotkit', // CopilotKit chat
  // Future namespaces can be added here:
  // IT_SUPPORT: 'it-support',
  // ONBOARDING: 'onboarding',
} as const;

export type ChatNamespaceKey =
  (typeof CHAT_NAMESPACES)[keyof typeof CHAT_NAMESPACES];

/**
 * Id of the client-only welcome message shown before a conversation has any
 * real messages (useCopilotKitAgent). Not persisted or eligible for feedback.
 */
export const WELCOME_MESSAGE_ID = 'welcome-message';

export type AiConversation = {
  conversationId: string;
  userId: string;
  organizationId: string;
  createdAt: string;
  expiresAt: string;
  updatedAt?: string;
};

export const GETTING_STARTED_STEPS = {
  CONNECT_HR_SYSTEM: 'connect_hr_system',
  ADD_EMPLOYEES: 'add_employees',
  INVITE_ADMINS: 'invite_admins',
  ADD_APPLICATIONS: 'add_applications',
  CONNECT_APP_INTEGRATION: 'connect_app_integration',
  ADD_GROUPS: 'add_groups',
  ENABLE_MDM: 'enable_mdm',
  ADD_PAYMENT_METHOD: 'add_payment_method',
  CONFIGURE_EMPLOYEE_SUPPORT: 'configure_employee_support',
  CONNECT_ABM: 'connect_abm',
  CONFIGURE_ZTP: 'configure_ztp',
  NOTIFY_TEAM: 'notify_team',
  INVITE_EMPLOYEES: 'invite_employees',
} as const;

export type GettingStartedStepKey =
  (typeof GETTING_STARTED_STEPS)[keyof typeof GETTING_STARTED_STEPS];

export type GettingStartedCompletionState = Record<
  GettingStartedStepKey,
  boolean
>;

/**
 * localStorage-backed progress flags for the Getting Started checklist.
 * Exported here (not from useGettingStartedStorage) so hooks outside
 * src/pages/ can import the type without a downward dependency.
 */
export type GettingStartedProgress = {
  skipAbm?: boolean;
  skipMacMdm?: boolean;
  skipItChampions?: boolean;
  skipHr?: boolean;
  startMacMdm?: boolean;
  skipGroups?: boolean;
  skipIntegrations?: boolean;
  skipPaymentMethod?: boolean;
  notifyTeam?: boolean;
  ztpConfigured?: boolean;
  inviteEmployees?: boolean;
};

/**
 * Reasons a user can select when down-voting a message
 * (MessageFeedbackActions' down-vote popover).
 */
export const FEEDBACK_REASONS = {
  IncorrectInformation: 'incorrect_information',
  DidNotAnswer: 'did_not_answer',
  WrongAction: 'wrong_action',
} as const;

export type FeedbackReason =
  (typeof FEEDBACK_REASONS)[keyof typeof FEEDBACK_REASONS];

/**
 * Committed feedback state for a single message, keyed by message id
 * in messageFeedbackSlice. Also the shape returned by the
 * updateMessageFeedback mutation (gigawattBackendApiSlice).
 */
export type MessageFeedbackEntry = {
  rating: 'up' | 'down' | null;
  reasons: FeedbackReason[];
};

/**
 * State passed to Gigawatt AI agent with browser and user context
 */
export type GigawattAgentState = {
  user: {
    timezone: string; // e.g., "America/New_York" from Intl.DateTimeFormat
    language: string; // e.g., "en-US" from user's preferred language settings
    userName?: string; // User's full name (e.g., "John Doe")
    employeeId?: string; // Employee ID for the current user
  };
  context: {
    currentPage: string; // Semantic identifier (e.g., "dashboard", "employees")
    currentPath: string; // Full path (e.g., "/organizations/123/employees")
    storefrontShoppingCountry?: ShoppingCountryValue; // Resolved Storefront shopping country; undefined if not yet resolved
  };
  lastToolset: string | null; // Last selected toolset (e.g., "application", "asset"); null until agent selects one
  getting_started_state?: GettingStartedCompletionState;
  getting_started_incomplete_steps?: GettingStartedStepKey[];
};
